import { format } from "date-fns";
import { Clock } from "lucide-react";
import { formatZonedTime, getTimeZoneAbbr } from "../../../lib/time";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Switch } from "../../../components/ui/switch";
import { VoteToggle } from "./vote-toggle";
import { SlotIndicator } from "./SlotIndicator.jsx";
import {
  hasUnacknowledgedRefIds,
  slotVisualsFor,
} from "../utils/slot-visuals.js";

export function VoteDialog({
  open,
  onOpenChange,
  modalDate,
  slots = [],
  displayTimeZone = null,
  showTimeZone = true,
  noTimesWork = false,
  canVote = false,
  onToggleNoTimesWork,
  draftVotes = {},
  pastSlotIds = new Set(),
  blockersBySlotId = {},
  blockerTitleBySchedulerId = {},
  onNavigateToSchedulerId,
  onSetVote,
  voteAutoSourceBySlotId = {},
  voteAutoReasonBySlotId = {},
  schedulerId = null,
  groupNames = {},
  onRestoreOverride,
  onConfirmOverride,
  autoVoteEnabled = false,
}) {
  const tzLabel = showTimeZone
    ? getTimeZoneAbbr(modalDate || new Date(), displayTimeZone)
    : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Cap height + scroll: a busy day's per-slot list can exceed the viewport
          (the dialog lists every slot on the day), which otherwise pushes the
          footer "Done"/"Save" actions off-screen and out of reach. */}
      <DialogContent className="max-h-[90vh] overflow-y-auto max-w-md">
        <DialogHeader>
          <DialogTitle>
            Vote for {modalDate ? format(modalDate, "MMM d") : ""}
            {tzLabel ? ` ${tzLabel}` : ""}
          </DialogTitle>
          <DialogDescription>
            Toggle Feasible or Preferred for each slot.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between rounded-2xl border border-slate-200/70 bg-white px-4 py-3 text-xs font-semibold text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
          <span>No times work for me</span>
          <Switch
            checked={noTimesWork}
            disabled={!canVote}
            onCheckedChange={onToggleNoTimesWork}
          />
        </div>
        <div className="mt-4 space-y-3">
          {slots.length === 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No slots on this day.
            </p>
          )}
          {slots.map((slot) => {
            const vote = draftVotes[slot.id];
            const isPast = pastSlotIds.has(slot.id);
            const blocker = blockersBySlotId?.[slot.id] || null;
            const blockerTitle = blocker?.sourceSchedulerId
              ? blockerTitleBySchedulerId?.[blocker.sourceSchedulerId] || null
              : null;
            const slotLabel = formatZonedTime(new Date(slot.start), displayTimeZone, "h:mm a", {
              showTimeZone,
            });
            const autoSource = voteAutoSourceBySlotId?.[slot.id] || null;
            const autoReason = voteAutoReasonBySlotId?.[slot.id] || null;
            const showIndicator = Boolean(autoReason?.kind);
            // Chunk G: vote-dialog rows are plain divs (no RBC), so apply
            // the full slot-visuals triple. Past slots get the
            // .scheduler-slot-past class (grayscale + opacity +
            // pointer-events:none) via fillClass — G3 dropped the prior
            // red wash so red is reserved for the engine override
            // treatment in G6.
            //
            // Chunk G6 + H12: synthesize the urgent-override engineKind so the
            // cell glow turns red. Same logic mirrored at SchedulerPage
            // eventPropGetter + list view; the SlotIndicator computes its
            // own copy from voteAutoSource so the icon flips red too.
            // H12: gated on autoVoteEnabled — if user turned off auto-vote,
            // downgrade urgent → amber conflict treatment.
            const isUrgentOverride =
              autoVoteEnabled &&
              autoSource === "auto-conflict-override" &&
              autoReason?.kind === "conflict" &&
              hasUnacknowledgedRefIds(autoReason);
            const engineKind = isUrgentOverride
              ? "conflict-override-urgent"
              : autoReason?.kind || null;
            const { fillClass, glowClass, borderClass } = slotVisualsFor({
              voteState: vote || null,
              engineKind,
              autoSource,
              isPast,
            });
            return (
              <div
                key={slot.id}
                data-testid={`vote-dialog-slot-${slot.id}`}
                title={isPast ? "This time slot is in the past." : undefined}
                className={`grid gap-2 rounded-2xl border border-slate-200/70 px-4 py-3 dark:border-slate-700 ${fillClass} ${glowClass} ${borderClass}`}
              >
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {slotLabel}
                  </p>
                  {isPast ? (
                    <span
                      data-testid={`vote-dialog-slot-past-marker-${slot.id}`}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400"
                    >
                      <Clock className="h-3 w-3" aria-hidden="true" />
                      <span>Past</span>
                    </span>
                  ) : (
                    showIndicator && (
                      <SlotIndicator
                        reason={autoReason}
                        voteAutoSource={autoSource}
                        onRestore={onRestoreOverride}
                        onConfirm={onConfirmOverride}
                        schedulerId={schedulerId}
                        slotId={slot.id}
                        displayTimeZone={displayTimeZone}
                        groupNames={groupNames}
                      />
                    )
                  )}
                </div>
                {blocker && (
                  <div className="text-xs text-slate-600 dark:text-slate-300">
                    <span className="font-semibold text-amber-600 dark:text-amber-400">Conflict</span>
                    {blocker?.sourceSchedulerId && onNavigateToSchedulerId && (
                      <button
                        type="button"
                        className="ml-2 font-semibold text-brand-primary hover:underline"
                        onClick={() => onNavigateToSchedulerId(blocker.sourceSchedulerId)}
                      >
                        View {blockerTitle ? `"${blockerTitle}"` : "blocking session"}
                      </button>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                  <span>Feasible</span>
                  <VoteToggle
                    checked={vote === "FEASIBLE" || vote === "PREFERRED"}
                    disabled={!canVote || noTimesWork || vote === "PREFERRED" || isPast}
                    label={`Toggle feasible vote for ${slotLabel}`}
                    testId={`vote-dialog-feasible-${slot.id}`}
                    onChange={(checked) => onSetVote(slot.id, checked ? "FEASIBLE" : null)}
                  />
                </div>
                <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                  <span>Preferred</span>
                  <VoteToggle
                    checked={vote === "PREFERRED"}
                    disabled={!canVote || noTimesWork || isPast}
                    label={`Toggle preferred vote for ${slotLabel}`}
                    testId={`vote-dialog-preferred-${slot.id}`}
                    onChange={(checked) => onSetVote(slot.id, checked ? "PREFERRED" : null)}
                  />
                </div>
                {isPast && (
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                    This slot is in the past.
                  </p>
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-full bg-brand-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90"
          >
            Done
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
